/**
 * Formik-bound QueryBuilder wrapper for policy categories, per-configuration and global.
 * Sibling of `ExposureConditionsQueryBuilderFormik` — same skeleton (read
 * `values[fieldId]`, gate errors on `submitCount`, fetch the catalog, fire
 * `onFieldsLoaded`, render the pure QB).
 *
 * Differences from the exposures sibling:
 *  - No prerequisite gate at all. The category dialog opens from a specific
 *    incident-configuration row, so the scope is a fixed input rather than
 *    form fields the user might not have picked yet.
 *  - Reads the caller's own catalog route rather than the shared
 *    `rules_engine/condition_fields` one, so `rule_set_type`, `event_key`, LOB
 *    and sub-org scope are all derived server-side and none are sent from here.
 *    That shared route is gated on SYSCONFIG_AUTOMATION, which the Policy
 *    Configuration screen does not require, and it takes the scope from its
 *    caller — so the clicked summary row's single sub-org would decide the
 *    catalog while the save validates against every sub-org the configuration
 *    is linked to. A global category's catalog is likewise its own route, scoped
 *    to the organization.
 */

import React, { useEffect } from 'react';
import type { RuleGroupType } from 'react-querybuilder';
import { getIn, useFormikContext } from 'formik';

import RuleConditionsQueryBuilder from '../../AutomaticRulesConfiguration/Conditions/QueryBuilder/RuleConditionsQueryBuilder';
import type { FieldDefinitionDto } from '../../AutomaticRulesConfiguration/Conditions/QueryBuilder/types';
import { useConditionFieldsFetcher } from '../../AutomaticRulesConfiguration/Conditions/QueryBuilder/useConditionFieldsFetcher';

interface PolicyCategoryConditionsQueryBuilderFormikProps {
  /** Formik path holding the `conditions_json` tree (default: `'conditions_json'`). */
  fieldId?: string;
  /** The categories route's own `condition_fields` catalog — per configuration, or the
   *  organization's global one. */
  catalogUrl: string;
  emptyMessage: string;
  /** Lets the parent form mirror the registry into its own state for use in
   *  Formik's form-level `validate` prop. */
  onFieldsLoaded?: (fields: FieldDefinitionDto[]) => void;
  /** Surfaces the catalog-fetch loading state so the dialog can disable Save
   *  while fields are still in flight. */
  onLoadingChange?: (isLoading: boolean) => void;
}

const NO_QUERY_PARAMS = {};

const PolicyCategoryConditionsQueryBuilderFormik: React.FC<PolicyCategoryConditionsQueryBuilderFormikProps> = ({
  fieldId = 'conditions_json',
  catalogUrl,
  emptyMessage,
  onFieldsLoaded,
  onLoadingChange,
}) => {
  const { values, setFieldValue, submitCount } = useFormikContext();
  const value = (getIn(values, fieldId) as RuleGroupType | null | undefined) ?? null;

  const { fields, isLoading } = useConditionFieldsFetcher({
    url: catalogUrl,
    enabled: true,
    queryParams: NO_QUERY_PARAMS,
  });

  // Intentionally only depends on `fields` — `onFieldsLoaded` identity is
  // irrelevant. Including it would silently infinite-loop if a caller passes
  // a non-memoized inline arrow.
  useEffect(() => {
    onFieldsLoaded?.(fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <RuleConditionsQueryBuilder
      value={value}
      onChange={(q) => setFieldValue(fieldId, q)}
      shouldShowErrors={submitCount > 0}
      fields={fields}
      isLoading={isLoading}
      prerequisiteMessage={null}
      emptyMessage={emptyMessage}
    />
  );
};

export default PolicyCategoryConditionsQueryBuilderFormik;
